import { create } from "zustand";
import { persist } from "zustand/middleware";

interface SavedSearch {
  query: string;
  label?: string;
  savedAt: number;
}

export type SearchMode = "classic" | "advanced";

interface PreferencesState {
  blacklist: string[];
  savedSearches: SavedSearch[];
  pausePreviews: boolean;
  analyticsOptOut: boolean;
  searchMode: SearchMode;
  gifsOnly: boolean;
  includeImages: boolean;

  addToBlacklist: (tag: string) => void;
  removeFromBlacklist: (tag: string) => void;
  clearBlacklist: () => void;

  saveSearch: (query: string, label?: string) => void;
  removeSavedSearch: (query: string) => void;

  setPausePreviews: (paused: boolean) => void;
  setAnalyticsOptOut: (optOut: boolean) => void;
  setSearchMode: (mode: SearchMode) => void;
  setGifsOnly: (only: boolean) => void;
  setIncludeImages: (include: boolean) => void;

  exportPreferences: () => string;
  importPreferences: (json: string) => void;
}

export const usePreferences = create<PreferencesState>()(
  persist(
    (set, get) => ({
      blacklist: [],
      savedSearches: [],
      pausePreviews: false,
      analyticsOptOut: false,
      searchMode: "classic" as SearchMode,
      gifsOnly: false,
      includeImages: false,

      addToBlacklist: (tag) =>
        set((s) => ({
          blacklist: s.blacklist.includes(tag) ? s.blacklist : [...s.blacklist, tag],
        })),

      removeFromBlacklist: (tag) =>
        set((s) => ({ blacklist: s.blacklist.filter((t) => t !== tag) })),

      clearBlacklist: () => set({ blacklist: [] }),

      saveSearch: (query, label) =>
        set((s) => {
          const filtered = s.savedSearches.filter((ss) => ss.query !== query);
          return {
            savedSearches: [{ query, label, savedAt: Date.now() }, ...filtered].slice(0, 50),
          };
        }),

      removeSavedSearch: (query) =>
        set((s) => ({ savedSearches: s.savedSearches.filter((ss) => ss.query !== query) })),

      setPausePreviews: (paused) => set({ pausePreviews: paused }),

      setAnalyticsOptOut: (optOut) => set({ analyticsOptOut: optOut }),

      setSearchMode: (mode) => set({ searchMode: mode }),

      setGifsOnly: (only) => set({ gifsOnly: only }),

      setIncludeImages: (include) => set({ includeImages: include }),

      exportPreferences: () => {
        const { blacklist, savedSearches, pausePreviews, analyticsOptOut } = get();
        return JSON.stringify({ blacklist, savedSearches, pausePreviews, analyticsOptOut }, null, 2);
      },

      importPreferences: (json) => {
        try {
          const data = JSON.parse(json);
          set({
            blacklist: Array.isArray(data.blacklist) ? data.blacklist : [],
            savedSearches: Array.isArray(data.savedSearches) ? data.savedSearches : [],
            pausePreviews: !!data.pausePreviews,
            analyticsOptOut: !!data.analyticsOptOut,
          });
        } catch {
          // malformed import — ignore
        }
      },
    }),
    {
      name: "gif-search-prefs",
    }
  )
);
